// Aggregate map-control reductions over a set of analysis rounds.
//
// The Analysis view has already fetched and parsed every filtered round, so this runs on the main
// thread against those in-memory replays and yields between chunks rather than paying to serialise
// thirty ParsedReplays into a worker.
//
// Everything here reduces the per-cell control field to something small and honest:
//   - a per-second T/CT/contested share timeline  (the engine already computes the triple)
//   - a per-second owner per callout area         (drives the round strip - a log, not an estimate)
//   - the first second each side takes an area    (first-TAKEN only; see the note on first-lost)
//   - whether an area changed hands with no fight over it
//
// Two deliberate constraints:
//
//  - Only HARD control counts as "taken". Implicit control carries a ~2s proof delay on the way in
//    and up to 25s of claim memory on the way out, so "turned our colour" is not "we hold it".
//  - Only the TAKEN direction is reported. First-LOST is smeared by that same claim memory, so a
//    "when did they lose X" number would be measuring the engine's decay constant, not the play.

import { createMapControlEngine, S_T_HARD, S_T_IMP, S_CT_HARD, S_CT_IMP, S_CONTEST } from '../MatchPage/Replay/mapControl/mapControlEngine';
import type { ControlGrid } from '../MatchPage/Replay/mapControl/mapControlGrid';
import type { ParsedReplay } from '../MatchPage/Replay/replayParser';

export const OWN_NONE = 0, OWN_T = 1, OWN_CT = 2, OWN_CONTEST = 3;

export interface AreaShape {
    name: string;
    points?: Array<{ x: number; y: number }>;
    minX?: number; maxX?: number; minY?: number; maxY?: number;
    centerX?: number; centerY?: number; radius?: number;
}

export interface RoundReduction {
    key: string;
    roundNumber: number;
    /** side the perspective team played this round, read from the demo roster, never from round number */
    ourSide: 'T' | 'CT';
    won: boolean | null;
    seconds: number;
    /** per second, share of walkable cells */
    shareT: Float32Array;
    shareCT: Float32Array;
    shareContested: Float32Array;
    /** areaCount * seconds, values OWN_* */
    areaOwner: Uint8Array;
    /**
     * areaCount, second at which T / CT first hard-held the area. -1 never, -2 already theirs when
     * the round began. The -2 case is not a take: everything within HARD_SECS of a spawning player
     * is hard-held at t=0, so without separating it every spawn-adjacent callout reports "taken in
     * 16 of 16 rounds, median 0s", which measures spawn geometry rather than anything the team did.
     */
    firstTakeT: Int16Array;
    firstTakeCT: Int16Array;
    /** areaCount, 1 if the area passed from one side to the other with no contested second between */
    concededUncontested: Uint8Array;

    // ---- per-cell accumulators, for the map views -------------------------------------------
    /** samples in this round where the cell was T / CT held, or contested */
    cellT: Uint16Array;
    cellCT: Uint16Array;
    cellContested: Uint16Array;
    /** total samples the round contributed, the denominator for the three above */
    samples: number;
    /** per cell, second at which T / CT first hard-held it. -1 never, -2 held from the whistle */
    cellFirstT: Int16Array;
    cellFirstCT: Int16Array;
    /** per cell, last second each side hard-held it (-1 never). With cellFirst* this gives a
     *  taken/held/left answer for any region the user selects. */
    cellLastT: Int16Array;
    cellLastCT: Int16Array;
    /** M * NUM_BUCKETS counts, for focusing the map on part of the round */
    cellTB: Uint8Array;
    cellCTB: Uint8Array;
    cellConB: Uint8Array;
    /** samples contributed to each bucket */
    samplesB: Uint16Array;
    /** cell index -> coarse bucket-grid index */
    coarseOf: Int32Array;
}

/**
 * Which of a side's players were standing in a region at a given second.
 *
 * Deliberately a PRESENCE reading, not a causal one: it says who was there when the ground turned,
 * not who caused it to turn. Attributing space to a player properly needs a counterfactual re-run,
 * and this is the honest cheap version — so the UI should say "who was there", never "who won it".
 */
export function playersInRegion(
    replay: ParsedReplay,
    side: 'T' | 'CT',
    atSecond: number,
    bounds: { minX: number; maxX: number; minY: number; maxY: number },
    zMin?: number,
    zMax?: number,
): string[] {
    const frames = replay.frames;
    if (!frames.length) return [];
    const startTick = frames[0].tick;
    const wantTick = startTick + atSecond * 64;
    // nearest frame at or after the moment
    let best = frames[0];
    for (const f of frames) { best = f; if (f.tick >= wantTick) break; }
    const out: string[] = [];
    for (let k = 0; k < replay.roster.length && k < best.players.length; k++) {
        if (replay.roster[k].team !== side) continue;
        const p = best.players[k];
        if (!p || !p.isAlive || (p.x === 0 && p.y === 0)) continue;
        if (zMin !== undefined && p.z < zMin) continue;
        if (zMax !== undefined && p.z > zMax) continue;
        if (p.x < bounds.minX || p.x > bounds.maxX || p.y < bounds.minY || p.y > bounds.maxY) continue;
        out.push(replay.roster[k].steamId);
    }
    return out;
}

/** Map each walkable cell to a callout area index, or -1. World-space test. */
export function buildCellAreas(grid: ControlGrid, cells: Int32Array, areas: AreaShape[]): Int16Array {
    const { G, cell, cfg } = grid;
    const out = new Int16Array(cells.length).fill(-1);
    // Areas overlap on several maps; the smallest containing one is the specific callout, so prefer
    // it. Resolving by file order instead attributes roughly a tenth of the map by authoring accident.
    const areaSize = areas.map(a => {
        if (a.points && a.points.length > 2) {
            let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
            for (const p of a.points) { minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x); minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y); }
            return (maxX - minX) * (maxY - minY);
        }
        if (a.radius) return Math.PI * a.radius * a.radius;
        if (a.minX !== undefined && a.maxX !== undefined && a.minY !== undefined && a.maxY !== undefined) {
            return (a.maxX - a.minX) * (a.maxY - a.minY);
        }
        return Infinity;
    });

    const inArea = (a: AreaShape, x: number, y: number): boolean => {
        if (a.points && a.points.length > 2) {
            let inside = false;
            const pts = a.points;
            for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
                const xi = pts[i].x, yi = pts[i].y, xj = pts[j].x, yj = pts[j].y;
                if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-9) + xi) inside = !inside;
            }
            return inside;
        }
        if (a.radius && a.centerX !== undefined && a.centerY !== undefined) {
            const dx = x - a.centerX, dy = y - a.centerY;
            return dx * dx + dy * dy <= a.radius * a.radius;
        }
        if (a.minX !== undefined && a.maxX !== undefined && a.minY !== undefined && a.maxY !== undefined) {
            return x >= a.minX && x <= a.maxX && y >= a.minY && y <= a.maxY;
        }
        return false;
    };

    for (let i = 0; i < cells.length; i++) {
        const c = cells[i], gx = c % G, gy = (c - (c % G)) / G;
        const wx = cfg.pos_x + (gx + 0.5) * cell;
        const wy = cfg.pos_y - (gy + 0.5) * cell;
        let best = -1, bestSize = Infinity;
        for (let a = 0; a < areas.length; a++) {
            if (areaSize[a] >= bestSize) continue;
            if (inArea(areas[a], wx, wy)) { best = a; bestSize = areaSize[a]; }
        }
        out[i] = best;
    }
    return out;
}

// The replay overlay samples every 16 ticks (250ms) because it is animating. Aggregates are read as
// whole seconds, so half that rate is invisible in the output and halves the compute.
const STRIDE_TICKS = 32;

// Per-cell counts are also kept in coarse time buckets so the map views can be focused on a slice of
// the round honestly. Whole-round totals scaled by a time fraction would render an identical picture
// and merely look like filtering.
// 5s granularity so a window like 22-30s is expressible. Held at HALF spatial resolution: full-res
// 5s buckets would be ~512KB per round, and this page already keeps every parsed replay in memory.
// Half-res is 128KB and the map layers are smoothed at roughly that scale anyway.
export const BUCKET_SECS = 5;
export const NUM_BUCKETS = 24;  // 0-5, 5-10, ... 115+
export const BUCKET_CELL_STRIDE = 2;

export interface ComputeOpts {
    grid: ControlGrid;
    cellArea: Int16Array;
    areaCount: number;
    /** hide players outside this z band, for split-level maps */
    zMin?: number;
    zMax?: number;
    /** fraction of an area's cells one side must hard-hold before it counts as taken */
    takeFraction?: number;
    onProgress?: (done: number, total: number) => void;
    signal?: { cancelled: boolean };
}

export interface RoundInput {
    key: string;
    roundNumber: number;
    replay: ParsedReplay;
    ourSide: 'T' | 'CT';
    won: boolean | null;
}

/**
 * Runs the control engine over each round and reduces it. Yields to the event loop between rounds so
 * a 30-round analysis does not freeze the page.
 */
export async function computeRoundReductions(rounds: RoundInput[], opts: ComputeOpts): Promise<RoundReduction[]> {
    const { grid, cellArea, areaCount, zMin, zMax } = opts;
    const takeFraction = opts.takeFraction ?? 0.5;
    const eng = createMapControlEngine(grid);
    const M = eng.M;

    // coarse cell index for the time-bucketed layers (half resolution, see BUCKET_CELL_STRIDE)
    const G = grid.G, GC = Math.ceil(G / BUCKET_CELL_STRIDE);
    const coarseOf = new Int32Array(M);
    for (let i = 0; i < M; i++) {
        const c = eng.cells[i], gx = c % G, gy = (c - (c % G)) / G;
        coarseOf[i] = ((gy / BUCKET_CELL_STRIDE) | 0) * GC + ((gx / BUCKET_CELL_STRIDE) | 0);
    }
    const COARSE = GC * GC;

    // cells per area, for the majority test
    const areaCells: number[][] = Array.from({ length: areaCount }, () => []);
    for (let i = 0; i < M; i++) { const a = cellArea[i]; if (a >= 0) areaCells[a].push(i); }

    const out: RoundReduction[] = [];
    for (let r = 0; r < rounds.length; r++) {
        if (opts.signal?.cancelled) break;
        const { key, roundNumber, replay, ourSide, won } = rounds[r];
        const frames = replay.frames;
        if (!frames.length) { opts.onProgress?.(r + 1, rounds.length); continue; }

        eng.reset();
        const startTick = frames[0].tick;
        const totalSecs = Math.max(1, Math.ceil((frames[frames.length - 1].tick - startTick) / 64) + 1);

        const shareT = new Float32Array(totalSecs);
        const shareCT = new Float32Array(totalSecs);
        const shareContested = new Float32Array(totalSecs);
        const areaOwner = new Uint8Array(areaCount * totalSecs);
        const firstTakeT = new Int16Array(areaCount).fill(-1);
        const firstTakeCT = new Int16Array(areaCount).fill(-1);
        const conceded = new Uint8Array(areaCount);
        const seenContested = new Uint8Array(areaCount);
        const lastOwnerSeen = new Uint8Array(areaCount);
        const startHeldT = new Uint8Array(areaCount), startHeldCT = new Uint8Array(areaCount);
        const cellT = new Uint16Array(M), cellCT = new Uint16Array(M), cellContested = new Uint16Array(M);
        const cellFirstT = new Int16Array(M).fill(-1), cellFirstCT = new Int16Array(M).fill(-1);
        const cellLastT = new Int16Array(M).fill(-1), cellLastCT = new Int16Array(M).fill(-1);
        let samples = 0;
        const cellTB = new Uint8Array(COARSE * NUM_BUCKETS), cellCTB = new Uint8Array(COARSE * NUM_BUCKETS);
        const cellConB = new Uint8Array(COARSE * NUM_BUCKETS), samplesB = new Uint16Array(NUM_BUCKETS);
        let firstSample = true;
        const filled = new Uint8Array(totalSecs);

        let prevTick = startTick;
        for (let i = 0; i < frames.length; i++) {
            const fr = frames[i];
            if (i !== 0 && fr.tick - prevTick < STRIDE_TICKS) continue;
            const dt = Math.max(1, fr.tick - prevTick);
            prevTick = fr.tick;

            // Stop at the first team wipe. Once a side is dead the map changes hands with nobody
            // left to fight for it, so every eliminated round would otherwise report its whole
            // remaining ground as "conceded uncontested" - which read as 22 of 24 rounds on the
            // first real dataset, i.e. the metric was mostly measuring who lost the round.
            let aliveT = 0, aliveCT = 0;
            for (let k = 0; k < replay.roster.length && k < fr.players.length; k++) {
                const p = fr.players[k];
                if (!p || !p.isAlive || (p.x === 0 && p.y === 0)) continue;
                if (replay.roster[k].team === 'T') aliveT++; else aliveCT++;
            }
            if (i !== 0 && (aliveT === 0 || aliveCT === 0)) break;

            const useFrame = (zMin !== undefined || zMax !== undefined)
                ? {
                    ...fr,
                    players: fr.players.map(p => {
                        if (!p || !p.isAlive) return p;
                        if (zMin !== undefined && p.z < zMin) return { ...p, isAlive: false };
                        if (zMax !== undefined && p.z > zMax) return { ...p, isAlive: false };
                        return p;
                    }),
                }
                : fr;

            const states = eng.step(useFrame as typeof fr, replay.roster, fr.tick, dt, []);
            const sec = Math.min(totalSecs - 1, Math.max(0, Math.floor((fr.tick - startTick) / 64)));

            const bucket = Math.min(NUM_BUCKETS - 1, Math.floor(sec / BUCKET_SECS));
            samplesB[bucket]++;
            let tc = 0, cc = 0, xc = 0;
            for (let c = 0; c < M; c++) {
                const s = states[c];
                if (s === S_T_HARD || s === S_T_IMP) { tc++; cellT[c]++; const k = coarseOf[c] * NUM_BUCKETS + bucket; if (cellTB[k] < 255) cellTB[k]++; }
                else if (s === S_CT_HARD || s === S_CT_IMP) { cc++; cellCT[c]++; const k = coarseOf[c] * NUM_BUCKETS + bucket; if (cellCTB[k] < 255) cellCTB[k]++; }
                else if (s === S_CONTEST) { xc++; cellContested[c]++; const k = coarseOf[c] * NUM_BUCKETS + bucket; if (cellConB[k] < 255) cellConB[k]++; }
                if (s === S_T_HARD) {
                    if (firstSample) cellFirstT[c] = -2;
                    else if (cellFirstT[c] === -1) cellFirstT[c] = sec;
                    cellLastT[c] = sec;
                }
                if (s === S_CT_HARD) {
                    if (firstSample) cellFirstCT[c] = -2;
                    else if (cellFirstCT[c] === -1) cellFirstCT[c] = sec;
                    cellLastCT[c] = sec;
                }
            }
            samples++;
            shareT[sec] = tc / M; shareCT[sec] = cc / M; shareContested[sec] = xc / M;
            filled[sec] = 1;

            for (let a = 0; a < areaCount; a++) {
                const list = areaCells[a];
                if (!list.length) continue;
                let t = 0, ct = 0, con = 0, tHard = 0, ctHard = 0;
                for (const ci of list) {
                    const s = states[ci];
                    if (s === S_T_HARD) { t++; tHard++; }
                    else if (s === S_T_IMP) t++;
                    else if (s === S_CT_HARD) { ct++; ctHard++; }
                    else if (s === S_CT_IMP) ct++;
                    else if (s === S_CONTEST) con++;
                }
                const n = list.length;
                let owner: number = OWN_NONE;
                if (con / n >= 0.25) owner = OWN_CONTEST;
                else if (t > ct && t / n >= 0.4) owner = OWN_T;
                else if (ct > t && ct / n >= 0.4) owner = OWN_CT;
                areaOwner[a * totalSecs + sec] = owner;

                if (firstSample) {
                    // held from the whistle - spawn geometry, not a take
                    if (tHard / n >= takeFraction) { startHeldT[a] = 1; firstTakeT[a] = -2; }
                    if (ctHard / n >= takeFraction) { startHeldCT[a] = 1; firstTakeCT[a] = -2; }
                }
                if (!startHeldT[a] && tHard / n >= takeFraction && firstTakeT[a] === -1) firstTakeT[a] = sec;
                if (!startHeldCT[a] && ctHard / n >= takeFraction && firstTakeCT[a] === -1) firstTakeCT[a] = sec;

                // handed over with no fight over it at any point in between
                if (owner === OWN_CONTEST) seenContested[a] = 1;
                if (owner === OWN_T || owner === OWN_CT) {
                    const prev = lastOwnerSeen[a];
                    if (prev && prev !== owner && !seenContested[a]) conceded[a] = 1;
                    if (prev !== owner) seenContested[a] = 0;
                    lastOwnerSeen[a] = owner;
                }
            }
            firstSample = false;
        }

        // carry the last computed value across seconds the stride skipped, so strips have no gaps
        for (let s = 1; s < totalSecs; s++) {
            if (filled[s]) continue;
            shareT[s] = shareT[s - 1]; shareCT[s] = shareCT[s - 1]; shareContested[s] = shareContested[s - 1];
            for (let a = 0; a < areaCount; a++) areaOwner[a * totalSecs + s] = areaOwner[a * totalSecs + s - 1];
        }

        out.push({
            key, roundNumber, ourSide, won, seconds: totalSecs,
            shareT, shareCT, shareContested, areaOwner,
            firstTakeT, firstTakeCT, concededUncontested: conceded,
            cellT, cellCT, cellContested, samples, cellFirstT, cellFirstCT, cellLastT, cellLastCT,
            cellTB, cellCTB, cellConB, samplesB, coarseOf,
        });

        opts.onProgress?.(r + 1, rounds.length);
        await new Promise(res => setTimeout(res, 0));
    }
    return out;
}
